/**
 * `downloadFile()` — first-class helper for `responseType: 'blob'` exports
 * with `content-disposition` filename extraction. ADR 113 §"Implementation
 * notes" item 1 calls this out explicitly: SMS's `apiClient.request<T>`
 * couldn't surface the server-suggested filename because it returned only
 * the parsed body. The shared lib bakes the helper in so every consumer
 * gets the same behavior.
 *
 * Routes through the configured ky instance so auth-refresh, GPC, and
 * vendor-org middleware apply identically to JSON requests.
 */
import type { KyInstance } from 'ky';

export interface DownloadResult {
  blob: Blob;
  filename: string;
}

export interface DownloadFileOptions {
  /** HTTP method. Defaults to GET; pass 'POST' for export endpoints that take a body. */
  method?: 'GET' | 'POST';
  /** Optional request body (POST exports use this). Strings/objects are JSON-stringified. */
  body?: BodyInit | Record<string, unknown> | null;
  /** Extra headers to merge in. */
  headers?: Record<string, string>;
  /** Filename to fall back to when the response carries no Content-Disposition. */
  fallbackFilename?: string;
}

/**
 * Build a `downloadFile(endpoint, options, fallbackFilename)` helper bound
 * to the given ky instance. The lib's `createClient` exposes one of these
 * via the returned client object (see `client.ts`).
 *
 * Returns `{ blob, filename }`. Throws on non-2xx responses (the auth-refresh
 * middleware has already had a chance to retry; a thrown error here means
 * the download genuinely failed).
 */
export function buildDownloadFile(
  ky: KyInstance,
): (
  endpoint: string,
  options?: DownloadFileOptions,
  fallbackFilename?: string,
) => Promise<DownloadResult> {
  return async (
    endpoint: string,
    options: DownloadFileOptions = {},
    fallbackFilename = 'download',
  ): Promise<DownloadResult> => {
    const method = options.method ?? 'GET';
    const headers = { ...(options.headers ?? {}) };
    let body: BodyInit | undefined;
    if (options.body !== undefined && options.body !== null) {
      if (
        typeof options.body === 'string' ||
        options.body instanceof FormData ||
        options.body instanceof URLSearchParams ||
        options.body instanceof Blob ||
        options.body instanceof ArrayBuffer
      ) {
        body = options.body as BodyInit;
      } else {
        body = JSON.stringify(options.body);
        if (!headers['Content-Type']) {
          headers['Content-Type'] = 'application/json';
        }
      }
    }

    const response = await ky(endpoint.replace(/^\/+/, ''), {
      method,
      headers,
      body,
    });

    const blob = await response.blob();
    const contentDisposition = response.headers.get('content-disposition');
    const filename =
      contentDisposition
        ?.split('filename=')[1]
        ?.trim()
        .replace(/["']/g, '') || (options.fallbackFilename ?? fallbackFilename);

    return { blob, filename };
  };
}
